import React, {useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {useQuery, useMutation, useQueryClient} from '@tanstack/react-query';
import {toast} from 'react-toastify';
import {Trash2, FlaskConical} from 'lucide-react';
import ProviderLayout from '@/modules/provider-console/components/ProviderLayout';
import Card from '@/shared/components/Card';
import {ProviderOrderTestsService} from '@/modules/order-tests/services/providerOrderTestsService';
import PatientSearch from '@/modules/order-tests/components/PatientSearchModal';
import PatientEditModal, {PatientEditButton} from '@/modules/provider-console/components/PatientEditModal';
import type {OrderTestsPatientSearchItem} from '@/shared/types/api';

const ProviderOrderTests: React.FC = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [searchResetKey, setSearchResetKey] = useState(0);
    const [editingPatientUserId, setEditingPatientUserId] = useState<number | null>(null);

    const queueQuery = useQuery({
        queryKey: ['order-tests-queue'],
        queryFn: () => ProviderOrderTestsService.getQueue(),
    });

    // Ensures the patient exists in our system and is in the queue, returns userId
    const ensurePatient = async (patient: OrderTestsPatientSearchItem): Promise<number> => {
        if (patient.dashboardUserId) {
            // Existing patient — add to queue so the order builder can find them
            await ProviderOrderTestsService.addToQueue(patient.dashboardUserId);
            return patient.dashboardUserId;
        }
        // ELIS-only patient — pull into our system (createPatientAndQueue adds to queue internally)
        const result = await ProviderOrderTestsService.createPatientAndQueue({
            firstName: patient.firstName,
            lastName: patient.lastName,
            email: '',
            birthDate: patient.birthDate,
            gender: patient.gender,
            elisPatientId: patient.elisPatientId,
        });
        return result.userId;
    };

    const saveToQueueMutation = useMutation({
        mutationFn: async (patient: OrderTestsPatientSearchItem) => {
            if (patient.dashboardUserId) {
                await ProviderOrderTestsService.addToQueue(patient.dashboardUserId);
                return;
            }
            // ELIS-only — creates user + queues in one call
            await ProviderOrderTestsService.createPatientAndQueue({
                firstName: patient.firstName,
                lastName: patient.lastName,
                email: '',
                birthDate: patient.birthDate,
                gender: patient.gender,
                elisPatientId: patient.elisPatientId,
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ['order-tests-queue']});
            setSearchResetKey((k) => k + 1);
            toast.success('Patient saved to queue');
        },
        onError: () => {
            toast.error('Failed to save patient to queue');
        },
    });

    const createOrderMutation = useMutation({
        mutationFn: (patient: OrderTestsPatientSearchItem) => ensurePatient(patient),
        onSuccess: async (userId) => {
            // Invalidate queue cache so the order builder sees the newly-queued patient
            await queryClient.invalidateQueries({queryKey: ['order-tests-queue']});
            navigate(`/provider/order-tests/new/${userId}`);
        },
        onError: () => {
            toast.error('Failed to start lab order');
        },
    });

    const createPatientMutation = useMutation({
        mutationFn: (patient: {
            firstName: string;
            lastName: string;
            email: string;
            birthDate: string;
            gender: string;
        }) => ProviderOrderTestsService.createPatientAndQueue(patient),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ['order-tests-queue']});
            setSearchResetKey((k) => k + 1);
            toast.success('Patient created and added to queue');
        },
        onError: () => {
            toast.error('Failed to create patient');
        },
    });

    const removeMutation = useMutation({
        mutationFn: (userId: number) => ProviderOrderTestsService.removeFromQueue(userId),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ['order-tests-queue']});
        },
    });

    const queue = queueQuery.data?.queue ?? [];
    const isBusy = saveToQueueMutation.isPending || createOrderMutation.isPending;

    return (
        <ProviderLayout
            title="Order Tests"
            subtitle="Create and manage lab orders"
>
            <div className="mx-auto max-w-4xl space-y-6 px-4 pb-6">
                {/* Search / add patient card */}
                <Card>
                    <Card.Header>
                        <div>
                            <Card.Title>New Lab Order</Card.Title>
                            <p className="mt-0.5 text-sm text-gray-500">
                                Search for an existing patient or create a new one
                            </p>
                        </div>
                    </Card.Header>
                    <Card.Content>
                        <PatientSearch
                            key={searchResetKey}
                            onCreateOrder={(patient) => createOrderMutation.mutate(patient)}
                            onSaveToQueue={(patient) => saveToQueueMutation.mutate(patient)}
                            onCreatePatient={(patient) => createPatientMutation.mutate(patient)}
                            isAdding={isBusy}
                            isCreating={createPatientMutation.isPending}
                        />
                    </Card.Content>
                </Card>

                {/* Queue */}
                <Card>
                    <Card.Header>
                        <div className="flex items-center gap-2">
                            <Card.Title>Order Queue</Card.Title>
                            <span className="inline-flex items-center rounded-full bg-mld-primary/10 px-2 py-0.5 text-[10px] font-semibold text-mld-primary">
                                {queue.length}
                            </span>
                        </div>
                    </Card.Header>
                    <Card.Content>
                        {queueQuery.isLoading ? (
                            <p className="text-sm text-gray-500">Loading queue...</p>
                        ) : queueQuery.isError ? (
                            <p className="text-sm text-mld-danger">Failed to load queue.</p>
                        ) : queue.length === 0 ? (
                            <p className="py-6 text-center text-sm text-gray-400">
                                No patients in queue. Search above to add one.
                            </p>
                        ) : (
                            <div className="divide-y divide-gray-100">
                                {queue.map((patient) => (
                                    <div key={patient.queueId} className="flex items-center gap-4 py-3">
                                        <div className="min-w-0 flex-1">
                                            <p className="text-sm font-medium text-gray-900">
                                                {patient.firstName} {patient.lastName}
                                            </p>
                                            <p className="text-xs text-gray-500">
                                                DOB: {patient.birthDate || 'N/A'}
                                                {patient.gender ? ` · ${patient.gender}` : ''}
                                                {' · Added '}
                                                {new Date(patient.addedAt).toLocaleDateString()}
                                            </p>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => navigate(`/provider/order-tests/new/${patient.userId}`)}
                                            className="inline-flex items-center gap-1.5 rounded-md bg-mld-primary/5 px-3 py-1.5 text-xs font-medium text-mld-primary ring-1 ring-mld-primary/20 hover:bg-mld-primary/10 transition-colors">
                                            <FlaskConical className="h-3.5 w-3.5" />
                                            Order
                                        </button>
                                        <PatientEditButton
                                            onClick={() => setEditingPatientUserId(patient.userId)}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => removeMutation.mutate(patient.userId)}
                                            disabled={removeMutation.isPending}
                                            className="rounded-md p-1.5 text-gray-400 hover:bg-mld-danger/5 hover:text-mld-danger transition-colors">
                                            <Trash2 className="h-4 w-4" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </Card.Content>
                </Card>
                <PatientEditModal
                    isOpen={editingPatientUserId != null}
                    userId={editingPatientUserId}
                    onClose={() => setEditingPatientUserId(null)}
                    onSaved={() => {
                        // Refetch the queue so DOB/gender/name update in the row.
                        queryClient.invalidateQueries({queryKey: ['order-tests-queue']});
                    }}
                />
            </div>
        </ProviderLayout>
    );
};

export default ProviderOrderTests;
